// Synthesized reveal sound (no audio files). Web Audio API.
// unlockAudio() must run inside a user gesture (join/reveal tap) so mobile browsers
// allow the later host-triggered fanfare to play.

let ctx = null;
let master = null;
const active = new Set();

// Real recorded audio (decoded into the same AudioContext so mute + unlock apply).
const clips = {};
let clipsLoading = false;
let countdownSrc = null;
const CLIP_URLS = { countdown: "/sounds/countdown.mp3", fanfare: "/sounds/fanfare.mp3" };

async function loadClips() {
  const c = ensureCtx();
  if (!c || clipsLoading) return;
  clipsLoading = true;
  await Promise.all(Object.entries(CLIP_URLS).map(async ([name, url]) => {
    if (clips[name]) return;
    try {
      const res = await fetch(`${process.env.PUBLIC_URL || ""}${url}`);
      const ab = await res.arrayBuffer();
      clips[name] = await c.decodeAudioData(ab);
    } catch (e) { console.debug("clip load failed", name, e); }
  }));
}

function playClip(name, gain = 1) {
  const c = ensureCtx();
  if (!c || !clips[name]) return null;
  const src = c.createBufferSource();
  src.buffer = clips[name];
  const g = c.createGain();
  g.gain.value = gain;
  src.connect(g); g.connect(master);
  src.start();
  active.add(src);
  src.onended = () => active.delete(src);
  return src;
}

const MUTE_KEY = "unveil_muted";
export function getMuted() {
  try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
}
export function setMuted(v) {
  try { localStorage.setItem(MUTE_KEY, v ? "1" : "0"); } catch (e) { console.debug("mute pref persist failed", e); }
  if (master) master.gain.value = v ? 0 : 1;
}

function ensureCtx() {
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return null;
    ctx = new AC();
    master = ctx.createGain();
    master.gain.value = getMuted() ? 0 : 1;
    master.connect(ctx.destination);
  }
  return ctx;
}

export function unlockAudio() {
  const c = ensureCtx();
  if (c && c.state === "suspended") c.resume().catch(() => {});
  loadClips();
}

function tone({ freq, start = 0, dur = 0.2, type = "sine", gain = 0.2, glideTo = null }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + start;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (glideTo) osc.frequency.exponentialRampToValueAtTime(glideTo, t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g); g.connect(master);
  osc.start(t0); osc.stop(t0 + dur + 0.05);
  active.add(osc);
  osc.onended = () => active.delete(osc);
}

// Rising suspense pad during the "are you ready" build phase.
export function playBuildup(duration = 2.4) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(80, t0);
  osc.frequency.exponentialRampToValueAtTime(320, t0 + duration);
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(500, t0);
  lp.frequency.exponentialRampToValueAtTime(2200, t0 + duration);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(0.12, t0 + duration * 0.8);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(lp); lp.connect(g); g.connect(master);
  osc.start(t0); osc.stop(t0 + duration + 0.1);
  active.add(osc);
  osc.onended = () => active.delete(osc);
}

// Countdown beep. step 3 -> low, 1 -> high, to build tension.
export function playTick(step) {
  if (clips.countdown) {
    // real 3-2-1 countdown clip plays once, covering all three ticks
    if (step === 3) countdownSrc = playClip("countdown", 0.9);
    return;
  }
  const map = { 3: 392, 2: 523, 1: 659 };
  const freq = map[step] || 440;
  tone({ freq, dur: 0.16, type: "triangle", gain: 0.28 });
  tone({ freq: freq * 2, dur: 0.08, type: "sine", gain: 0.1 });
}

// A single brass/trumpet-like note: detuned sawtooths + vibrato through a
// filter that "blooms" on the attack, giving a bright, punchy fanfare tone.
function brassNote({ freq, start = 0, dur = 0.4, gain = 0.3 }) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + start;
  const sum = c.createGain();
  const oscs = [];
  [-5, 6].forEach((detune) => {
    const osc = c.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.setValueAtTime(freq, t0);
    osc.detune.setValueAtTime(detune, t0);
    osc.connect(sum);
    osc.start(t0); osc.stop(t0 + dur + 0.1);
    active.add(osc);
    osc.onended = () => active.delete(osc);
    oscs.push(osc);
  });
  // vibrato for a lively brass feel
  const lfo = c.createOscillator();
  const lfoGain = c.createGain();
  lfo.type = "sine";
  lfo.frequency.setValueAtTime(5.5, t0);
  lfoGain.gain.setValueAtTime(freq * 0.007, t0);
  lfo.connect(lfoGain);
  oscs.forEach((o) => lfoGain.connect(o.frequency));
  lfo.start(t0); lfo.stop(t0 + dur + 0.1);
  active.add(lfo);
  lfo.onended = () => active.delete(lfo);
  // brassy filter that opens fast on the attack, then settles
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.Q.setValueAtTime(3, t0);
  lp.frequency.setValueAtTime(freq * 1.4, t0);
  lp.frequency.linearRampToValueAtTime(Math.min(freq * 7, 9000), t0 + 0.05);
  lp.frequency.linearRampToValueAtTime(freq * 2.6, t0 + dur);
  // punchy envelope: fast attack, slight decay to a sustain, then release
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.035);
  g.gain.exponentialRampToValueAtTime(gain * 0.75, t0 + 0.13);
  g.gain.setValueAtTime(gain * 0.75, t0 + Math.max(0.14, dur * 0.65));
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  sum.connect(lp); lp.connect(g); g.connect(master);
}

// Timpani / bass-drum thump for dramatic weight.
function bassThump(start = 0, gain = 0.45) {
  const c = ensureCtx();
  if (!c) return;
  const t0 = c.currentTime + start;
  const osc = c.createOscillator();
  const g = c.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(170, t0);
  osc.frequency.exponentialRampToValueAtTime(45, t0 + 0.18);
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.4);
  osc.connect(g); g.connect(master);
  osc.start(t0); osc.stop(t0 + 0.45);
  active.add(osc);
  osc.onended = () => active.delete(osc);
}

// Dramatic trumpet fanfare + whoosh on the reveal.
export function playFanfare() {
  const c = ensureCtx();
  if (!c) return;
  if (clips.fanfare) {
    // real trumpet fanfare; stop the countdown clip so they don't overlap
    if (countdownSrc) { try { countdownSrc.stop(); } catch (e) { /* already stopped */ } countdownSrc = null; }
    playClip("fanfare", 1);
    return;
  }
  // whoosh (noise sweep down)
  const dur = 0.55;
  const buffer = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  const noise = c.createBufferSource();
  noise.buffer = buffer;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.setValueAtTime(3000, c.currentTime);
  bp.frequency.exponentialRampToValueAtTime(400, c.currentTime + dur);
  const ng = c.createGain();
  ng.gain.setValueAtTime(0.22, c.currentTime);
  ng.gain.exponentialRampToValueAtTime(0.0001, c.currentTime + dur);
  noise.connect(bp); bp.connect(ng); ng.connect(master);
  noise.start(); noise.stop(c.currentTime + dur);

  const G4 = 392, C5 = 523.25, E5 = 659.25, G5 = 783.99, C6 = 1046.5, E6 = 1318.5, G6 = 1568.0, C4 = 261.63;
  // Phrase 1 — the call: quick triplet rising to a held note.
  const call = [[G4, 0.0, 0.14], [C5, 0.15, 0.14], [E5, 0.30, 0.14], [G5, 0.45, 0.5]];
  // Phrase 2 — the triumph: rising run into a big held finale.
  const triumph = [[C5, 1.05, 0.12], [E5, 1.18, 0.12], [G5, 1.31, 0.14], [C6, 1.48, 1.35]];
  [...call, ...triumph].forEach(([f, s, d]) => brassNote({ freq: f, start: s, dur: d, gain: 0.32 }));
  // fat final chord + octave body for a triumphant wall of brass
  brassNote({ freq: E6, start: 1.5, dur: 1.3, gain: 0.15 });
  brassNote({ freq: G5, start: 1.5, dur: 1.3, gain: 0.15 });
  brassNote({ freq: C5, start: 1.48, dur: 1.35, gain: 0.2 });
  brassNote({ freq: C4, start: 1.48, dur: 1.35, gain: 0.16 });
  // timpani hits: one on the opening, a big one on the finale
  bassThump(0.44, 0.32);
  bassThump(1.46, 0.5);
  // high shimmer on top of the finale
  [C6, E6, G6].forEach((f) => tone({ freq: f, start: 1.75, dur: 1.0, type: "sine", gain: 0.07 }));
}

export function stopAll() {
  active.forEach((n) => { try { n.stop(); } catch {} });
  active.clear();
}
